
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useOrganizationSync } from "./use-organization-sync";
import { resolveUniqueSlug } from "@/utils/uniqueSlug";

export interface CreateCompanyData {
  name: string;
  enable_customer_admin?: boolean;
  enable_device_diagnosis?: boolean;
  enable_api_configuration?: boolean;
  website?: string;
  industry?: string;
  address?: string;
  country?: string;
  division?: string;
  notes?: string;
  logo_url?: string;
  description?: string;
  opening_times?: string;
  marketing_banner_url?: string;
  image_1_url?: string;
  image_2_url?: string;
  image_3_url?: string;
  google_places_id?: string;
  slug?: string;
  visibility?: 'public' | 'private';
  internal_owner_id?: string;
  external_owner_id?: string;
  enable_fleet_mode?: boolean;
  // MSA fields
  has_msa?: boolean;
  msa_reference?: string;
  // Custom AI Workflow fields
  custom_workflow_id?: string;
  custom_workflow_description?: string;
  // Optional showcase access code
  showcase_access_code?: string | null;
  showcase_access_code_enabled?: boolean;
}

export const useCompanies = () => {
  return useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select(`
          *,
          api_token_status,
          api_token_created_at,
          api_token_last_used_at,
          internal_owner:internal_owner_id(
            id,
            first_name,
            last_name,
            email
          ),
          external_owner:external_owner_id(
            id,
            first_name,
            last_name,
            email
          )
        `)
        .neq('id', '00000000-0000-0000-0000-000000000000') // Hide default partner org
        .is('parent_organization_id' as any, null) // Sub-customers are not CRM customers
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
};

export const useOrganizations = () => {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select(`
          *,
          api_token_status,
          api_token_created_at,
          api_token_last_used_at,
          internal_owner:internal_owner_id(
            id,
            first_name,
            last_name,
            email
          ),
          external_owner:external_owner_id(
            id,
            first_name,
            last_name,
            email
          )
        `)
        .neq('id', '00000000-0000-0000-0000-000000000000') // Hide default partner org
        .is('parent_organization_id' as any, null) // Sub-customers are not CRM customers
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
};

export const useCreateCompany = (options?: { syncToBuildhip?: boolean }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const organizationSync = useOrganizationSync();

  return useMutation({
    mutationFn: async (data: CreateCompanyData) => {
      // Clean owner IDs - convert "none" to null for UUID fields
      const cleanedData = {
        ...data,
        internal_owner_id: data.internal_owner_id === 'none' ? null : data.internal_owner_id,
        external_owner_id: data.external_owner_id === 'none' ? null : data.external_owner_id,
      };

      // Auto-generate slug if not provided
      let finalSlug = cleanedData.slug;
      if (!finalSlug) {
        finalSlug = await resolveUniqueSlug(cleanedData.name);
      }

      const { data: company, error } = await supabase
        .from('organizations')
        .insert({ ...cleanedData, slug: finalSlug })
        .select()
        .single();

      if (error) throw error;
      return company;
    },
    onSuccess: async (company, data) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: "Success",
        description: "Organization created successfully",
      });

      // Optionally sync to BuildShip after successful creation
      if (options?.syncToBuildhip && company.id) {
        try {
          await organizationSync.mutateAsync({
            organizationId: company.id,
            profileData: data,
            showSuccessToast: false // Don't show duplicate success toast
          });
          console.log('✅ Organization creation sync completed');
        } catch (syncError) {
          console.error('❌ Organization creation sync failed:', syncError);
          // Don't show error toast as the main operation succeeded
        }
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create organization",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateCompany = (options?: { syncToBuildhip?: boolean }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const organizationSync = useOrganizationSync();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CreateCompanyData }) => {
      // Clean owner IDs - convert "none" to null for UUID fields
      let updateData = {
        ...data,
        internal_owner_id: data.internal_owner_id === 'none' ? null : data.internal_owner_id,
        external_owner_id: data.external_owner_id === 'none' ? null : data.external_owner_id,
      };

      // Auto-generate slug if name changed and slug not provided
      if (data.name && !data.slug) {
        updateData.slug = await resolveUniqueSlug(data.name, id);
      }

      const { data: company, error } = await supabase
        .from('organizations')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return company;
    },
    onSuccess: async (company, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: "Success",
        description: "Organization updated successfully",
      });

      // Optionally sync to BuildShip after successful update
      if (options?.syncToBuildhip && id) {
        try {
          await organizationSync.mutateAsync({
            organizationId: id,
            profileData: data,
            showSuccessToast: false // Don't show duplicate success toast
          });
          console.log('✅ Organization update sync completed');
        } catch (syncError) {
          console.error('❌ Organization update sync failed:', syncError);
          // Don't show error toast as the main operation succeeded
        }
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update organization",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: "Success",
        description: "Organization deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete organization",
        variant: "destructive",
      });
    },
  });
};
